import Form, { FormInstance } from 'antd/lib/form'
import { ActionButton } from 'Components/common/ActionButton/ActionButton'
import { Row } from 'antd/lib/grid'
import TextArea from 'antd/lib/input/TextArea'
import Radio from 'antd/lib/radio'
import Input from 'antd/lib/input'
import { useEffect, useMemo, useState } from 'react'
import Flex from 'antd/lib/flex'
import Tooltip from 'antd/lib/tooltip'
import InfoCircleFilled from '@ant-design/icons/lib/icons/InfoCircleFilled'
import { ActionModal } from 'Components/modal/ActionModal/ActionModal'
import classes from './HumanResponse.module.less'
import { useDialog } from 'Hooks/use-dialog'
import { HumanResponseFormType } from 'Types/Tasks.types'
import { CustomForm } from 'Types/CustomTask.types'

type HumanResponsePropsType = {
    form: FormInstance<HumanResponseFormType>
    onSubmitHumanResponse: () => void
    disableAddStep: boolean
    initialValues?: HumanResponseFormType
    isReviewMode?: boolean
    isReadOnly?: boolean
    isCorrectResponse?: boolean
    validateTurnResponseForm?: () => Promise<boolean>
    setForms: (forms: CustomForm) => void
}

export const HumanResponse = (props: HumanResponsePropsType) => {
    const {
        form,
        onSubmitHumanResponse,
        disableAddStep,
        isReviewMode,
        isReadOnly,
        validateTurnResponseForm,
        setForms,
    } = props
    const [isSubmitted, setIsSubmitted] = useState<boolean>(isReviewMode ?? false)
    const [manualStepsCount, setManualStepsCount] = useState<number>(
        props.initialValues?.values.length ?? 0
    )

    useEffect(() => {
        setForms({ humanResponseForm: form, finalSubmissionForm: form })
    }, [form, setForms])

    const submitModalDialog = useDialog(false)

    const stepContent = useMemo(() => {
        const stepContentHTML: JSX.Element[] = []
        if (!props.isCorrectResponse) {
            for (let i = 0; i < manualStepsCount; i++) {
                stepContentHTML.push(
                    <>
                        <div className={classes.stepContainer} key={i} data-testid="step-container">
                            <Form.Item<HumanResponseFormType>
                                name={['values', i, 'python_or_llm']}
                                label={
                                    <Flex vertical>
                                        <Flex gap={4}>
                                            <div>Is this step LLM based vs Python based?</div>
                                            <Tooltip
                                                placement="right"
                                                title={
                                                    'If the step has mathematical calculations or reasoning that a human can conduct without a calculator, such as (e.g., "if a=b and b>c, then a>c" or "2*5" ) it would be considered as an "LLM" step.If the step has calculations that requires a calculator such as (1.063^3), it would be considered as a "Python" step.We should keep the amount requiring "Python" limited.'
                                                }
                                            >
                                                <InfoCircleFilled />
                                            </Tooltip>
                                        </Flex>
                                        <div className={classes.subLabel}>
                                            Steps involving reasoning or simple math (ex. "if a`
                                            {'>'}
                                            `b`{'>'}`c, then a`{'>'}`c" or "2*5") are "LLM" based.
                                            More complex calculations needing a calculator (ex.
                                            1.063^3) are "Python" based.
                                        </div>
                                    </Flex>
                                }
                                rules={[{ required: true, message: 'Please select an option' }]}
                            >
                                <Radio.Group>
                                    <Radio value="PYTHON">Python</Radio>
                                    <Radio value="LLM">LLM</Radio>
                                </Radio.Group>
                            </Form.Item>
                            <Row>
                                <Form.Item<HumanResponseFormType>
                                    name={['values', i, 'correct_step']}
                                    label={'Correct step'}
                                    rules={[{ required: true, message: 'Enter correct step' }]}
                                    className={classes.textAreaFormItem}
                                >
                                    <TextArea rows={5} />
                                </Form.Item>
                            </Row>
                        </div>
                    </>
                )
            }
        }

        if (isSubmitted) {
            stepContentHTML.push(
                <Form.Item<HumanResponseFormType>
                    name={'human_final_answer'}
                    label={<p className={classes.inputTitle}>Final Short Answer</p>}
                    rules={[{ required: true, message: 'Final short answer is required' }]}
                >
                    <Input className={classes.input} placeholder="Eg: (5, x=4x)" />
                </Form.Item>
            )
        }

        return stepContentHTML
    }, [props.isCorrectResponse, isSubmitted, manualStepsCount])

    const actionButtons = useMemo(() => {
        if (isReviewMode || isSubmitted) {
            return null
        }

        return (
            <>
                <ActionButton
                    onClick={async () => {
                        if (manualStepsCount === 0) {
                            if (await validateTurnResponseForm?.()) {
                                submitModalDialog.show()
                            }
                            return
                        }
                        setManualStepsCount(prevCount => prevCount + 1)
                    }}
                    title="Add Step"
                    style={{ margin: '0 10px 0 0', borderRadius: 4, width: '150px' }}
                    disabled={disableAddStep}
                />
                <ActionButton
                    onClick={() => setIsSubmitted(true)}
                    title="Submit"
                    style={{ margin: 0, borderRadius: 4, width: '150px' }}
                    disabled={manualStepsCount === 0}
                />
            </>
        )
    }, [
        isReviewMode,
        isSubmitted,
        disableAddStep,
        manualStepsCount,
        submitModalDialog,
        validateTurnResponseForm,
    ])

    const submitModal = useMemo(() => {
        if (isSubmitted) {
            return null
        }

        return (
            <ActionModal
                getVisibility={() => submitModalDialog.isOpen}
                title={
                    <>
                        <InfoCircleFilled className={classes.infoIcon} />
                        Are you sure?
                    </>
                }
                content="You can not change above annotations afterwards."
                primaryButtonName={'Yes'}
                onConfirm={() => {
                    submitModalDialog.hide()
                    setManualStepsCount(prevCount => prevCount + 1)
                }}
                onCancel={() => submitModalDialog.hide()}
            ></ActionModal>
        )
    }, [isSubmitted, submitModalDialog])

    const sectionTitle = useMemo(() => {
        if (props.isReviewMode) {
            if (!props.isCorrectResponse) {
                return 'Human Response'
            }
        } else {
            return 'Section 3: PROVIDE CORRECT STEPS MANUALLY'
        }
    }, [props.isCorrectResponse, props.isReviewMode])

    return (
        <div className={classes.section}>
            <p className={classes.title}>{sectionTitle}</p>

            <Form
                form={form}
                layout="vertical"
                initialValues={props.initialValues ?? { values: [] }}
                onFinish={onSubmitHumanResponse}
                disabled={isReadOnly}
            >
                {stepContent}
            </Form>

            {actionButtons}
            {submitModal}
        </div>
    )
}
